/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Display de caja: total + ahorro de promos (plano, sin card).
 */
import React, { useMemo } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { styles } from '../posShared.styles';
import {
  CHECKOUT_HERO_PROMO_SCROLL_MAX_ROWS,
  formatCheckoutHeroPromoAmount,
  getCheckoutHeroMetaLines,
  resolveCheckoutHeroPromoDisplay,
} from './posCheckoutDerived';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import type { PosFiscalMode } from '../../domain/posTypes';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';

type Props = {
  total?: number;
  cartEvaluation?: CartEvaluationResult | null;
  saleFiscalMode?: PosFiscalMode;
  /** En wide el comprobante ya esta en la columna ticket. */
  showFiscalHint?: boolean;
  badgeText?: string;
  checkoutVisible?: boolean;
};

export function PosCheckoutTotalHero(props: Props) {
  const ctx = usePosCheckoutContextOptional();
  const {
    total = 0,
    cartEvaluation = null,
    saleFiscalMode = 'non_fiscal',
    showFiscalHint = true,
    badgeText,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const fiscalLines = getCheckoutHeroMetaLines(saleFiscalMode, { showFiscalHint });
  const promoDisplay = useMemo(
    () => resolveCheckoutHeroPromoDisplay(cartEvaluation),
    [cartEvaluation]
  );

  const savingsHint = (() => {
    if (promoDisplay.kind !== 'items' || promoDisplay.items.length === 0) return null;
    const promoCount = promoDisplay.items.length;
    const discountTotal = promoDisplay.items.reduce((sum, item) => sum + item.amount, 0);
    const promoWord = promoCount === 1 ? 'promo' : 'promos';
    return `Ahorro ${formatCheckoutHeroPromoAmount(discountTotal)} · ${promoCount} ${promoWord}`;
  })();

  return (
    <View style={styles.checkoutTotalHero} accessibilityRole="header">
      <View style={styles.checkoutTotalHeroMain}>
        <Text style={styles.checkoutHeroKicker}>A cobrar</Text>
        <Text style={styles.checkoutRegisterTotal}>
          {formatPosMoney(total)}
        </Text>
        {badgeText ? (
          <View style={styles.checkoutHeroBadge}>
            <Text style={styles.checkoutHeroBadgeText}>{badgeText}</Text>
          </View>
        ) : null}
        {savingsHint ? <Text style={styles.checkoutPayHeroHint}>{savingsHint}</Text> : null}

        {promoDisplay.kind === 'items' && promoDisplay.items.length > 0 ? (
          <View style={styles.checkoutAppliedPromosPanel} testID="checkout.applied-promos">
            {(() => {
              const rows = promoDisplay.items.map((item) => (
                <View key={item.id} style={promoStyles.row}>
                  <Text style={styles.checkoutAppliedPromosPreviewName} numberOfLines={1}>
                    {item.name}
                  </Text>
                  <Text style={styles.checkoutAppliedPromosDiscountAmount}>
                    {formatCheckoutHeroPromoAmount(item.amount)}
                  </Text>
                </View>
              ));
              // Más de CHECKOUT_HERO_PROMO_SCROLL_MAX_ROWS → la lista scrollea
              // dentro del panel para no empujar el total.
              if (promoDisplay.items.length > CHECKOUT_HERO_PROMO_SCROLL_MAX_ROWS) {
                return (
                  <ScrollView
                    style={styles.checkoutAppliedPromosScroll}
                    contentContainerStyle={styles.checkoutAppliedPromosScrollContent}
                    nestedScrollEnabled
                    showsVerticalScrollIndicator={false}
                  >
                    {rows}
                  </ScrollView>
                );
              }
              return <View style={styles.checkoutAppliedPromosList}>{rows}</View>;
            })()}
          </View>
        ) : null}

        {fiscalLines.length > 0 ? (
          <View style={[styles.checkoutTotalHeroMetaStack, styles.checkoutTotalHeroMetaStackFlush]}>
            {fiscalLines.map((line) => (
              <View key={line.label} style={styles.checkoutTotalHeroMetaRow}>
                <Text style={styles.checkoutTotalHeroMetaLabel}>{line.label}</Text>
                <Text style={styles.checkoutTotalHeroMetaValue} numberOfLines={2}>
                  {line.value}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const promoStyles = {
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
  },
} as const;
